import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    Chip,
    Badge,
    Divider,
    Tooltip,
} from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useEffect, useState, useMemo } from "react";
import { Get_URI } from "./config";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { language } from "../../Utils/Language";
import { toast } from "react-toastify";
import AddStockDialog from "../../Components/Dialogs/AddStock";
import InvoicePage from "./InvoiceDetails";
import { useSearchParams } from "react-router-dom";

const INVOICE_TYPE_LABEL = {
    "1": "Invoice with Tax",
    "2": "Invoice without Tax",
    "3": "Personal Sale",
};

const INVOICE_TYPE_COLOR = {
    "1": "#1976d2",
    "2": "#2e7d32",
    "3": "#7b1fa2",
};

const AddInvoice = ({
    initialInvoiceData = null,
    isEditMode = false,
    editInvoiceId = null,
}) => {
    const words = language();
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("");
    const [isAddStockDialogOpen, setIsAddStockDialogOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isRefresh, setIsRefresh] = useState(false);
    const [searchParams] = useSearchParams();

    const authHeader = useAuthHeader();
    const invoiceType = searchParams.get("invoiceType");
    const customerId = searchParams.get("selectedCustomer");

    const paramsKey = useMemo(
        () => ({ invoiceType, customerId }),
        [invoiceType, customerId]
    );

    const [invoice, setInvoice] = useState(
        isEditMode && initialInvoiceData
            ? {
                  otherPartyId: initialInvoiceData.otherPartyId,
                  taxPercentage: initialInvoiceData.taxPercentage || 0,
                  dollarToLiraRate: initialInvoiceData.dollarToLiraRate || 89500,
                  note: initialInvoiceData.note || "",
                  items: initialInvoiceData.items || [],
                  serialId: initialInvoiceData.serialId || null,
              }
            : {
                  otherPartyId: 0,
                  taxPercentage: 0,
                  dollarToLiraRate: 89500,
                  note: "",
                  items: [],
                  serialId: null,
              }
    );

    const [otherParties, setOtherParties] = useState([]);
    const [stockLocations, setStockLocations] = useState([]);
    const [confirmedSale, setConfirmedSale] = useState(false);
    const [invoiceItems, setInvoiceItems] = useState([]);

    // ── Derived totals ──────────────────────────────────────────────────────
    const subtotal = invoice.items.reduce(
        (acc, item) => acc + item.quantity * item.price - (item.discountAmount || 0),
        0
    );

    // ── Data fetching ────────────────────────────────────────────────────────
    useEffect(() => {
        const getOtherParities = async () => {
            try {
                const res = await fetch(Get_URI("GET_CUSTOMERS"), {
                    headers: { "Content-Type": "application/json", Authorization: authHeader },
                });
                if (res.ok) {
                    const d = await res.json();
                    setStockLocations(d.stockLocations);
                    setOtherParties(d.otherParties);
                }
            } catch {
                toast.error("Could not load customer data");
            }
        };

        const getItems = async () => {
            const isHiddenParam = invoiceType === "3" || (isEditMode && initialInvoiceData?.typeId === 3);
            try {
                const res = await fetch(
                    Get_URI("GET_ITEMS") + "?" + new URLSearchParams({ isHidden: isHiddenParam, customerId }),
                    { headers: { "Content-Type": "application/json", Authorization: authHeader } }
                );
                if (res.ok) {
                    const d = await res.json();
                    const enriched = d.data.map((item) => {
                        const remaining = item.Stock.reduce((s, st) => s + st.quantityRemaining, 0);
                        const specialPrice = item?.SpecialPricing?.find(
                            (sp) => sp.otherPartyId === Number(customerId)
                        );
                        return {
                            ...item,
                            remaining,
                            priceNormal: specialPrice ? Number(specialPrice.price) : item.priceNormal,
                        };
                    });
                    setItems(enriched);
                }
            } catch {
                toast.error("Could not load item list");
            }
        };

        getItems();
        getOtherParities();
    }, [authHeader, isRefresh, paramsKey]);

    useEffect(() => {
        setInvoiceItems(invoice.items);
    }, [confirmedSale, invoice.items]);

    // ── Item operations ──────────────────────────────────────────────────────
    const addItemToInvoice = (id) => {
        const itemIndex = items.findIndex((i) => i.id === id);
        const existingIndex = invoice.items.findIndex((i) => i.id === id);

        if (existingIndex !== -1) {
            const updated = { ...invoice };
            updated.items[existingIndex].quantity++;
            if (itemIndex !== -1) items[itemIndex].remaining--;
            setInvoice({ ...updated });
            setItems([...items]);
            return;
        }

        const src = items[itemIndex];
        if (!src) return;
        src.remaining--;
        setItems([...items]);
        setInvoice({
            ...invoice,
            items: [
                ...invoice.items,
                {
                    id,
                    name: src.name,
                    quantity: 1,
                    price: src.priceNormal,
                    discountAmount: 0,
                    discountPercentage: 0,
                    isTaxable: false,
                    lineId: `${id}-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
                },
            ],
        });
    };

    const addItemAsDifferent = (id) => {
        const src = items.find((i) => i.id === id);
        if (!src) return;
        src.remaining--;
        setItems([...items]);
        setInvoice({
            ...invoice,
            items: [
                ...invoice.items,
                {
                    id,
                    name: src.name,
                    quantity: 1,
                    price: src.priceNormal,
                    discountAmount: 0,
                    discountPercentage: 0,
                    isTaxable: false,
                    lineId: `${id}-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
                },
            ],
        });
    };

    const changeQty = (lineId, delta) => {
        const idx = invoice.items.findIndex((i) => i.lineId === lineId);
        if (idx === -1) return;
        const item = invoice.items[idx];
        const newQty = item.quantity + delta;
        if (newQty <= 0) {
            removeLineItem(idx, item);
            return;
        }
        const updated = [...invoice.items];
        updated[idx] = { ...item, quantity: newQty };
        setInvoice({ ...invoice, items: updated });

        // sync stock remaining
        const stockIdx = items.findIndex((i) => i.id === item.id);
        if (stockIdx !== -1) {
            items[stockIdx].remaining -= delta;
            setItems([...items]);
        }
    };

    const removeLineItem = (index, item) => {
        const updated = invoice.items.filter((_, i) => i !== index);
        setInvoice({ ...invoice, items: updated });

        const stockIdx = items.findIndex((i) => i.id === item.id);
        if (stockIdx !== -1) {
            items[stockIdx].remaining += item.quantity;
            setItems([...items]);
        }
    };

    const handleInvoiceChange = (e) => {
        const { name, value } = e.target;
        setInvoice({ ...invoice, [name]: value });
    };

    const filteredItems = items.filter((item) =>
        item.name.toLowerCase().includes(filter.toLowerCase())
    );

    const typeColor = INVOICE_TYPE_COLOR[invoiceType] || "#1976d2";
    const typeLabel = INVOICE_TYPE_LABEL[invoiceType] || "Invoice";

    if (confirmedSale) {
        return (
            <InvoicePage
                items={invoiceItems}
                setItems={setInvoiceItems}
                otherPartyId={customerId}
                authHeader={authHeader}
                invoice={invoice}
                typeId={invoiceType}
                isEditMode={isEditMode}
                editInvoiceId={editInvoiceId}
            />
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100vh", overflow: "hidden", bgcolor: "#f5f6fa" }}>
            {/* ── Top bar ───────────────────────────────────────────────── */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                    bgcolor: "white",
                    borderBottom: "1px solid #e0e0e0",
                    gap: 2,
                }}
            >
                <ReceiptLongIcon sx={{ color: typeColor }} />
                <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
                    New Sale
                </Typography>
                <Chip
                    label={typeLabel}
                    size="small"
                    sx={{ bgcolor: typeColor + "18", color: typeColor, fontWeight: 600, fontSize: 12 }}
                />
            </Box>

            {/* ── Main content ──────────────────────────────────────────── */}
            <Box sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>

                {/* ── LEFT: Stock catalog ─────────────────────────────── */}
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        borderRight: "1px solid #e0e0e0",
                        bgcolor: "white",
                        overflow: "hidden",
                    }}
                >
                    {/* Panel header */}
                    <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                            <InventoryIcon fontSize="small" sx={{ color: "text.secondary" }} />
                            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" letterSpacing={0.8} textTransform="uppercase">
                                Available Stock
                            </Typography>
                            <Badge badgeContent={filteredItems.length} color="primary" sx={{ ml: "auto" }} />
                        </Box>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search items..."
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {/* Items list */}
                    <Box sx={{ overflowY: "auto", flex: 1, px: 2, pb: 2 }}>
                        {filteredItems.length === 0 && (
                            <Box sx={{ py: 6, textAlign: "center", color: "text.disabled" }}>
                                <InventoryIcon sx={{ fontSize: 40, mb: 1 }} />
                                <Typography variant="body2">No items found</Typography>
                            </Box>
                        )}
                        {filteredItems.map((item) => {
                            const isLow = item.remaining <= item.minAlarmQuantity;
                            return (
                                <Box
                                    key={item.id}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        p: 1.5,
                                        mb: 1,
                                        borderRadius: 2,
                                        border: "1px solid",
                                        borderColor: isLow ? "warning.light" : "#e8eaf0",
                                        bgcolor: isLow ? "#fffbf0" : "#fafbfd",
                                        transition: "box-shadow 0.15s",
                                        "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                                    }}
                                >
                                    {/* Stock badge */}
                                    <Box
                                        sx={{
                                            minWidth: 44,
                                            height: 44,
                                            borderRadius: 1.5,
                                            bgcolor: isLow ? "warning.light" : "primary.light",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Typography variant="caption" fontWeight={700} color={isLow ? "warning.dark" : "primary.dark"} lineHeight={1}>
                                            {item.remaining}
                                        </Typography>
                                        <Typography sx={{ fontSize: 8, color: isLow ? "warning.dark" : "primary.dark" }}>
                                            left
                                        </Typography>
                                    </Box>

                                    {/* Item info */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap>
                                            {item.name}
                                        </Typography>
                                        {item.description && (
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                {item.description}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="primary" fontWeight={600}>
                                            ${Number(item.priceNormal).toFixed(2)}
                                        </Typography>
                                    </Box>

                                    {/* Action buttons */}
                                    <Box sx={{ display: "flex", gap: 0.5 }}>
                                        <Tooltip title="Add to invoice" arrow>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => addItemToInvoice(item.id)}
                                                disabled={item.remaining <= 0}
                                                sx={{
                                                    bgcolor: "primary.main",
                                                    color: "white",
                                                    borderRadius: 1.5,
                                                    "&:hover": { bgcolor: "primary.dark" },
                                                    "&:disabled": { bgcolor: "#ccc", color: "#888" },
                                                }}
                                            >
                                                <AddShoppingCartIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Add as separate line" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => addItemAsDifferent(item.id)}
                                                disabled={item.remaining <= 0}
                                                sx={{
                                                    bgcolor: "#f0f4ff",
                                                    color: "primary.main",
                                                    borderRadius: 1.5,
                                                    "&:hover": { bgcolor: "#dde6ff" },
                                                }}
                                            >
                                                <PlaylistAddIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Add stock" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => { setIsAddStockDialogOpen(true); setSelectedItem(item); }}
                                                sx={{
                                                    bgcolor: "#f0faf4",
                                                    color: "success.main",
                                                    borderRadius: 1.5,
                                                    "&:hover": { bgcolor: "#d4edda" },
                                                }}
                                            >
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* ── RIGHT: Invoice builder ───────────────────────────── */}
                <Box
                    sx={{
                        width: 420,
                        flexShrink: 0,
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "white",
                        overflow: "hidden",
                    }}
                >
                    {/* Invoice panel header */}
                    <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <Typography variant="subtitle2" fontWeight={700} color="text.secondary" letterSpacing={0.8} textTransform="uppercase">
                            Invoice Details
                        </Typography>
                    </Box>

                    {/* Invoice metadata */}
                    <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                        {invoiceType === "1" && (
                            <>
                                <TextField
                                    name="taxPercentage"
                                    label="Tax %"
                                    size="small"
                                    type="number"
                                    fullWidth
                                    margin="dense"
                                    onChange={handleInvoiceChange}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    }}
                                />
                                <TextField
                                    name="dollarToLiraRate"
                                    label="Exchange Rate (USD → LBP)"
                                    size="small"
                                    type="number"
                                    fullWidth
                                    margin="dense"
                                    value={invoice.dollarToLiraRate || ""}
                                    onChange={handleInvoiceChange}
                                    placeholder="e.g. 89500"
                                />
                            </>
                        )}
                        <TextField
                            name="note"
                            label="Note (optional)"
                            size="small"
                            type="text"
                            fullWidth
                            margin="dense"
                            onChange={handleInvoiceChange}
                            multiline
                            rows={2}
                        />
                    </Box>

                    <Divider />

                    {/* Selected items */}
                    <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" letterSpacing={0.5} textTransform="uppercase" fontSize={11}>
                                Items
                            </Typography>
                            {invoice.items.length > 0 && (
                                <Chip size="small" label={invoice.items.length} sx={{ ml: "auto", height: 18, fontSize: 11 }} />
                            )}
                        </Box>

                        {invoice.items.length === 0 && (
                            <Box sx={{ py: 5, textAlign: "center", color: "text.disabled" }}>
                                <AddShoppingCartIcon sx={{ fontSize: 36, mb: 1, opacity: 0.4 }} />
                                <Typography variant="body2" color="text.disabled">
                                    No items added yet
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                    Click the cart button on items to the left
                                </Typography>
                            </Box>
                        )}

                        {invoice.items.map((invoiceItem, index) => (
                            <Box
                                key={invoiceItem.lineId}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    py: 1,
                                    borderBottom: "1px solid #f5f5f5",
                                    "&:last-child": { borderBottom: "none" },
                                }}
                            >
                                {/* Name + price */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap>
                                        {invoiceItem.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        ${Number(invoiceItem.price).toFixed(2)} ea.
                                        {" · "}
                                        <strong>${(invoiceItem.quantity * invoiceItem.price).toFixed(2)}</strong>
                                    </Typography>
                                </Box>

                                {/* Qty controls */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <IconButton size="small" onClick={() => changeQty(invoiceItem.lineId, -1)}
                                        sx={{ bgcolor: "#f5f5f5", borderRadius: 1, p: 0.5 }}>
                                        <RemoveIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                    <Typography variant="body2" fontWeight={700} sx={{ minWidth: 24, textAlign: "center" }}>
                                        {invoiceItem.quantity}
                                    </Typography>
                                    <IconButton size="small" onClick={() => changeQty(invoiceItem.lineId, 1)}
                                        sx={{ bgcolor: "#f5f5f5", borderRadius: 1, p: 0.5 }}>
                                        <AddIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Box>

                                {/* Delete */}
                                <Tooltip title="Remove" arrow>
                                    <IconButton size="small" color="error" onClick={() => removeLineItem(index, invoiceItem)}
                                        sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ))}
                    </Box>

                    {/* Live total + CTA */}
                    <Box sx={{ px: 2, py: 2, borderTop: "1px solid #e0e0e0" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                                Subtotal ({invoice.items.length} {invoice.items.length === 1 ? "item" : "items"})
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                                ${subtotal.toFixed(2)}
                            </Typography>
                        </Box>
                        {invoiceType === "1" && invoice.taxPercentage > 0 && (
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Tax ({invoice.taxPercentage}%)
                                </Typography>
                                <Typography variant="body2">
                                    ~${((subtotal * (invoice.taxPercentage || 0)) / 100).toFixed(2)}
                                </Typography>
                            </Box>
                        )}
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={invoice.items.length === 0}
                            onClick={() => setConfirmedSale(true)}
                            sx={{ mt: 1.5, fontWeight: 700, py: 1.5, borderRadius: 2 }}
                            startIcon={<ReceiptLongIcon />}
                        >
                            Review & Create Invoice
                        </Button>
                    </Box>
                </Box>
            </Box>

            {isAddStockDialogOpen && (
                <AddStockDialog
                    item={selectedItem}
                    isOpen={isAddStockDialogOpen}
                    stockLocations={stockLocations}
                    onClose={() => {
                        setIsAddStockDialogOpen(false);
                        setSelectedItem(null);
                        setIsRefresh(!isRefresh);
                    }}
                    authHeader={authHeader}
                    otherParties={otherParties}
                />
            )}
        </Box>
    );
};

export default AddInvoice;
